import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  from,
  split,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { RetryLink } from "@apollo/client/link/retry";
import { WebSocketLink } from "@apollo/client/link/ws";

import { onError } from "@apollo/client/link/error";
import { toast } from "react-toastify";
import { getStoredProfile, useAuth } from "./auth/authContext";
import { apiUrl } from "./config";
import React, { FC, useEffect } from "react";

let connectingIssue = false;
let token: string | null | undefined;

const getToken = () => {
  const user = getStoredProfile("impersonate") || getStoredProfile("user");
  return user?.token;
};

const authLink = new ApolloLink((operation, forward) => {
  token = token || getToken();
  // add the authorization to the headers
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : null,
    },
  }));

  if (operation.variables) {
    const omitTypename = (key: string, value: unknown) =>
      key === "__typename" ? undefined : value;
    operation.variables = JSON.parse(
      JSON.stringify(operation.variables),
      omitTypename
    );
  }

  return forward(operation);
});

const wsLink = new WebSocketLink({
  uri: "wss://" + apiUrl + "/graphql",
  options: {
    // reconnect: true,
    // connectionParams: {
    //   authToken: getToken(),
    // },
    connectionParams: () => {
      return {
        authToken: getToken(),
      };
    },
    connectionCallback: (error: any) => {
      if (error?.message === "Your connection was rejected.") {
        //token likely expired/old. Get new one
        if (connectingIssue) return;

        console.error(error);

        if (!!token) {
          console.error(error?.message);
          connectingIssue = true;
        }
      }
    },
  },
});

const httpLink = new HttpLink({
  uri: "https://" + apiUrl + "/graphql",
});

// Route subscriptions over the WebSocket; send queries and mutations over HTTP.
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);

const errorLink = onError(
  ({ graphQLErrors, networkError, response, operation, forward }) => {
    console.log({ graphQLErrors, networkError, response, operation });

    if (graphQLErrors) {
      graphQLErrors.forEach(({ message }) => {
        toast(`${message}`, { type: toast.TYPE.ERROR, autoClose: 3000 });
      });
    }

    if (networkError) {
      toast(`Network Error: ${networkError}`, {
        type: toast.TYPE.WARNING,
        autoClose: 3000,
      });
    }

    if (response) response.errors = undefined;

    forward(operation);
  }
);

// Retry queries on connection failures only. A 4xx (e.g. 401 with no token) won't succeed on retry, and
// retrying a mutation whose response was lost could apply it twice.
const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      const isMutation = operation.query.definitions.some(
        (d) => d.kind === "OperationDefinition" && d.operation === "mutation"
      );
      const status = error?.statusCode as number | undefined;
      return !!error && !isMutation && !(status && status >= 400 && status < 500);
    },
  },
});

export const ApolloClientProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { setApolloClient: setAuthApolloClient } = useAuth();
  const [apolloClient, setApolloClientState] = React.useState<
    ApolloClient<NormalizedCacheObject> | undefined
  >(undefined);

  useEffect(() => {
    if (!apolloClient) {
      const apolloCli = new ApolloClient({
        cache: new InMemoryCache(),
        link: from([retryLink, errorLink, authLink, splitLink]),
      });
      setApolloClientState(apolloCli);
      setAuthApolloClient(apolloCli);
    }
  }, [apolloClient, setAuthApolloClient]);

  if (apolloClient) {
    return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
  }

  return <></>;
};
